import {MAX_REGEX_CAPTURE_GROUPS} from '../constants';

type CaptureGroupMetadata = {
  start: number;
  end: number;
  content: string;
};

export function validateMultiMetricQuickAddRegex(pattern: string): {
  errorMessage: string | null;
} {
  // Validate regex syntax
  try {
    new RegExp(pattern);
  } catch (error) {
    return {errorMessage: 'Invalid regex syntax.'};
  }

  // Validate no nested groups
  if (hasNestedGroups(pattern)) {
    return {errorMessage: 'Nested capture groups are not allowed.'};
  }

  // Validate num capture groups
  const captureGroups = extractCaptureGroups(pattern);
  if (captureGroups.length > MAX_REGEX_CAPTURE_GROUPS) {
    return {
      errorMessage: `Up to ${MAX_REGEX_CAPTURE_GROUPS} capture groups are allowed.`,
    };
  }

  return {errorMessage: null};
}

/**
 * Checks whether a regex pattern contains nested groups.
 * @example
 *   hasNestedGroups("foo/(b(ar))/baz") -> true
 *   hasNestedGroups("foo/(bar)/baz") -> false
 */
export function hasNestedGroups(pattern: string): boolean {
  let isInGroup = false;

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];

    // Ignore escaped characters
    if (char === '\\') {
      i++; // skips the next character, which is escaped
      continue;
    }

    // Check for start of group
    if (char === '(') {
      // Ignore non-capturing groups
      const isNonCapturing = pattern[i + 1] === '?' && pattern[i + 2] === ':';
      if (isNonCapturing) {
        i += 2; // skip next two chars (?:)
        continue;
      }

      // Opening paren while already in a group -> nested group found
      if (isInGroup) {
        return true;
      }

      isInGroup = true;
    } else if (isInGroup && char === ')') {
      isInGroup = false;
    }
  }

  return false;
}

/**
 * Extracts capture group positions from a regex pattern.
 * Assumes no nested groups.
 * @example
 *   extractCaptureGroups("foo/(.+)/(baz)") => [
 *     {start: 4, end: 8, content: ".+"},
 *     {start: 9, end: 14, content: "baz"},
 *   ]
 */
export function extractCaptureGroups(pattern: string): CaptureGroupMetadata[] {
  const groups: CaptureGroupMetadata[] = [];

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];

    // Ignore escaped characters
    if (char === '\\') {
      i++;
      continue;
    }

    // Ignore anything that's not the start of a group
    if (char !== '(') {
      continue;
    }

    if (char === '(') {
      // Ignore non-capturing groups
      const isNonCapturing = pattern[i + 1] === '?' && pattern[i + 2] === ':';
      if (isNonCapturing) {
        i += 2; // skip next two chars (?:)
        continue;
      }

      // Find closing paren
      let j = i + 1;
      while (j < pattern.length) {
        const innerChar = pattern[j];

        // Ignore escaped characters
        if (innerChar === '\\') {
          j += 2;
          continue;
        }

        // Stop when we find the closing paren
        if (innerChar === ')') {
          break;
        }

        j++;
      }

      // Add capture group
      const content = pattern.substring(i + 1, j);
      groups.push({start: i, end: j + 1, content});

      // Continue scanning from the end of the group
      i = j;
    }
  }

  return groups;
}
